// Shared navigation link configuration.
// Used by both the slide-in SideNav drawer and the persistent SiteHeader top bar,
// so the navigation can never drift between the two.
//
// Entry shape:
//   { href, label, icon, description?, cls?, badge?, menu? }
//   badge: boolean — shows a live unread-count badge on the inbox link.
//   menu:  a top-bar dropdown to put the link in (e.g. 'About') instead of
//          showing it flat. Presentation only: the link is still a top-bar link
//          as far as topBarLinksFrom and the tests are concerned.

export const STATIC_LINKS = [
  { href: '/',                     label: 'Home',               icon: '🏛️', description: 'Welcome to Per Ankh' },
  { href: '/events',               label: 'Events',             icon: '📅', description: 'Upcoming ceremonies & workshops' },
  { href: '/lectures',             label: 'Lectures',           icon: '🎙️', description: 'Talks from our elders and teachers' },
  { href: '/per-ankh',             label: 'Ethos & Structure',  icon: '📜', description: 'Our philosophy & organization', menu: 'About' },
  { href: '/ceremony-and-safety',  label: 'Ceremony & Safety',  icon: '🛡️', description: 'Sacred practices & guidelines', menu: 'About' },
  { href: '/donate',               label: 'Donate',             icon: '💛', description: 'Support our mission' },
  { href: '/contact',              label: 'Contact',            icon: '✉️', description: 'Get in touch', menu: 'About' },
  { href: '/calculator',           label: 'Calculator',         icon: '🔢', description: 'Dosage & timing tools', menu: 'About' },
];

export const GUEST_LINKS = [
  { href: '/become-a-member', label: 'Become a Member', icon: '🤝', description: 'Join our sacred community' },
  { href: '/member/login',    label: 'Member Sign In',  icon: '🔑', description: 'Access your member portal', cls: 'pa-side-link--cta' },
];

export const MEMBER_LINKS = [
  { href: '/member/dashboard', label: 'My Home',        icon: '✨', description: 'Your sacred member space', cls: 'pa-side-link--cta' },
  { href: '/member/feed',      label: 'Feed',           icon: '🔥', description: 'Share and reflect with everyone' },
  { href: '/member/circles',   label: 'Circles',        icon: '🫂', description: 'Small group rooms and study pods' },
  { href: '/member/inbox',     label: 'Inbox',          icon: '✉️', description: 'Direct messages & conversations', badge: true },
  { href: '/member/profile',   label: 'My Profile',     icon: '👤', description: 'View and edit your profile' },
  { href: '/member/pass',      label: 'My Pass',        icon: '🎫', description: 'Your QR code for checking in at the church' },
  { href: '/member/directory', label: 'Members',        icon: '👥', description: 'Browse the community' },
];

export const ADMIN_LINKS = [
  { href: '/admin/dashboard',  label: 'Admin Panel', icon: '⚙️', description: 'Review member applications' },
  { href: '/admin/moderation', label: 'Moderation',  icon: '🚩', description: 'Review flagged content' },
  { href: '/admin/agent',      label: 'Agent OS',    icon: '☥', description: 'Work with the assistant and Claude Code' },
  { href: '/admin/attendance', label: 'Attendance',  icon: '🎫', description: 'Door check-ins and greeters' },
];

/**
 * Links shown ONLY to signed-in approved members.
 *
 * These are members' rooms, so nothing about them should appear in navigation for
 * anyone else — not the drawer, not the desktop top bar. Both surfaces read this
 * list and gate it on the member role, so the two cannot drift. The Realm comes
 * with membership; the Lab and the Fungarium meet a member at the door and offer
 * the Study Pass if they have not bought it.
 */
export const MEMBER_ONLY_LINKS = [
  {
    href: '/realm',
    room: 'realm',
    label: 'The Realm',
    topBarLabel: 'Realm',
    icon: '🌌',
    description: 'Walk the world your study has grown',
  },
  {
    href: '/inner-alchemy',
    room: 'lab',
    label: 'Inner Alchemy Lab',
    topBarLabel: 'The Lab',
    icon: '☥',
    description: 'The living brain, neural chemistry & sacred botanicals',
  },
  {
    href: '/mycology',
    room: 'realm',
    label: 'Mycology Studies',
    topBarLabel: 'Mycology',
    icon: '🍄',
    description: 'The Fungarium — nineteen fields of fungal science, gamified',
  },
  {
    href: '/courses',
    room: 'realm',
    label: 'Courses',
    topBarLabel: 'Courses',
    icon: '📜',
    description: 'Taught by members of our community — growing, foraging, Reiki and more',
  },
];

// The primary links surfaced in the persistent Desktop top bar.
// Kept intentionally lean so the top bar stays clean and intuitive.
export const TOP_BAR_LINKS = [
  { href: '/',                     label: 'Home' },
  { href: '/events',               label: 'Events' },
  { href: '/per-ankh',             label: 'Ethos' },
  { href: '/ceremony-and-safety',  label: 'Safety' },
  { href: '/calculator',           label: 'Tools' },
  { href: '/contact',              label: 'Contact' },
  { href: '/donate',               label: 'Donate' },
];

// Primary call-to-action shown in the top bar.
export const TOP_BAR_CTA = {
  href: '/become-a-member',
  label: 'Join',
};

/**
 * Every navigation entry a given visitor should be offered, in one list.
 *
 * WHY THIS EXISTS. The two navigation surfaces used to be assembled separately —
 * the top bar took TOP_BAR_LINKS, the drawer took STATIC_LINKS + member links — and
 * they drifted badly. The desktop top bar ended up offering six links and no way to
 * reach Circles, the Feed or the Inbox at all, because the drawer's floating button
 * is hidden whenever the top bar is present. A member on a laptop simply could not
 * get to half the site.
 *
 * So both surfaces now build from this function. It is the only place that decides
 * what a visitor may see; a link added here appears in both, and neither can quietly
 * fall behind the other again.
 *
 * @param {{user?: Object|null, canOpenLabs?: boolean, canEnterRealm?: boolean}} opts
 * @returns {Array<{href: string, label: string, icon?: string, description?: string, cls?: string, badge?: boolean, group: string}>}
 */
export function navFor(opts) {
  // A default parameter only covers `undefined`, and destructuring `null` throws —
  // so normalise. A guest is the safe fallback: it is the least a visitor may see.
  const { user = null, canOpenLabs = false, canEnterRealm = canOpenLabs } = (opts && typeof opts === 'object') ? opts : {};
  const signedIn = Boolean(user);
  const role = user?.publicMetadata?.role;
  const isAdmin = role === 'admin';

  const links = [];

  // The public site. Same list on both surfaces, so nothing public is ever
  // reachable on a phone but missing on a laptop.
  for (const l of STATIC_LINKS) links.push({ ...l, group: 'public' });

  // The member's own area.
  if (signedIn) {
    for (const l of MEMBER_LINKS) links.push({ ...l, group: 'member' });
  } else {
    for (const l of GUEST_LINKS) links.push({ ...l, group: 'guest' });
  }

  // The members' rooms: shown only to an approved member. The Realm opens with
  // membership; the Lab and the Fungarium meet the member at the door and offer the
  // Study Pass. canOpenLabs is kept as the fallback the tests pass in.
  for (const l of MEMBER_ONLY_LINKS) {
    if (l.room === 'realm' ? canEnterRealm : canOpenLabs) links.push({ ...l, group: 'labs' });
  }

  if (isAdmin) {
    for (const l of ADMIN_LINKS) links.push({ ...l, group: 'admin' });
  }

  return links;
}

/**
 * The links the desktop top bar shows as its own flat items.
 *
 * The public set plus the members' rooms. Those two rooms stay flat deliberately:
 * they are the deepest, most-used part of the site for the people allowed in, and
 * there is a navigation test asserting they are directly reachable from the top bar
 * rather than buried in a menu.
 *
 * @param {Array} links from navFor
 * @returns {Array}
 */
export function topBarLinksFrom(links) {
  return links.filter((l) => l.group === 'public' || l.group === 'labs');
}

/**
 * Everything that belongs behind the desktop "Account" menu — the member's own
 * area, the admin tools, and (for a guest) the two ways in.
 *
 * This is what makes the desktop bar able to reach as much as the mobile drawer.
 * Previously these were drawer-only, and because the drawer's floating button is
 * hidden when the top bar is present, a member on a laptop had no route to Circles,
 * the Feed or the Inbox at all.
 *
 * @param {Array} links from navFor
 * @returns {Array}
 */
export function accountMenuLinksFrom(links) {
  return links.filter((l) => l.group === 'member' || l.group === 'guest' || l.group === 'admin');
}

/** Kept as the old name so existing callers keep working. */
export const memberMenuLinksFrom = accountMenuLinksFrom;
